var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import '@adyen/adyen-web/dist/adyen.css';
import styled from 'styled-components';
import { AsurionDiv } from '../../BaseComponents';
import { SupportedPaymentMethodMappings, SupportedPaymentMethods } from '../../../services/ApplicationSession';
import { BillingAddressField } from '../../BillingAddress/BillingAddress';
import { useCreditCardField } from '../../../services/BillingGatewayServiceHooks';
import { AsurionTheme } from '../../Theme';
import { getLocaleDetails } from '../../Internationalization';
import { SubmitButton } from '../../SubmitButton/SubmitButton';
import { ComponentId } from '../../../common/Constants';
import { getCardholderNameFromString } from '../CreditCard';
import { AddressErrorHandling } from '../../../services/ErrorService';
export var CreditCardRow = styled(AsurionDiv)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    display: flex;\n    flex-direction: row;\n    gap: ", ";\n\n    @media (max-width: 429px) {\n        flex-direction: column;\n    }\n"], ["\n    display: flex;\n    flex-direction: row;\n    gap: ", ";\n\n    @media (max-width: 429px) {\n        flex-direction: column;\n    }\n"])), function (props) { return props.theme.fieldGap; });
var CreditCardWithCardholderName = styled(AsurionDiv)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    display: flex;\n    flex-direction: column;\n    gap: ", ";\n"], ["\n    display: flex;\n    flex-direction: column;\n    gap: ", ";\n"])), function (props) { return props.theme.fieldGap; });
var CreditCard = forwardRef(function (props, ref) {
    var _a, _b, _c, _d, _e;
    var formRef = useRef(null);
    var localeDetails = getLocaleDetails(props.vendor, props.locale);
    var CreditCardFields = localeDetails.creditCardComponent;
    var creditCardLocaleConfig = localeDetails.creditCard;
    var cardFieldStyling = {
        base: {
            color: 'black',
            fontSmoothing: 'antialiased',
            fontFamily: AsurionTheme.fontFamily,
            fontSize: '1rem',
            fontWeight: 300,
            fontStretch: 'semi-condensed'
        }
    };
    var _f = useCreditCardField(formRef, props.applicationSession, cardFieldStyling, props.locale, props.onCreditCardInitialized), selectedCardBrand = _f.selectedCardBrand, creditCardPayload = _f.creditCardPayload, showExpiryDateContents = _f.showExpiryDateContents, expiryDateEmpty = _f.expiryDateEmpty, showSecurityCodeContents = _f.showSecurityCodeContents, securityCodeEmpty = _f.securityCodeEmpty, showCardNumberContents = _f.showCardNumberContents, cardNumberEmpty = _f.cardNumberEmpty, supportedCardBrands = _f.supportedCardBrands, cardValidityState = _f.cardValidityState;
    var _g = useState(undefined), cardholderName = _g[0], setCardholderName = _g[1];
    var _h = useState(props.existingBillingInformation), billingInformation = _h[0], setBillingInformation = _h[1];
    var _j = useState(undefined), billingAddressValidityState = _j[0], setBillingAddressValidityState = _j[1];
    var _k = useState(undefined), validityState = _k[0], setValidityState = _k[1];
    // Used for custom continue button
    useImperativeHandle(ref, function () { return ({
        continue: onClickCallback
    }); });
    useEffect(function () {
        if (props.existingCardholderName !== undefined) {
            if (typeof props.existingCardholderName === 'string') {
                setCardholderName(props.existingCardholderName);
            }
            else {
                setCardholderName("".concat(props.existingCardholderName.firstName || '', " ").concat(props.existingCardholderName.lastName || '').trim());
            }
        }
    }, [props.existingCardholderName]);
    useEffect(function () {
        if (cardholderName) {
            setBillingInformation(__assign(__assign({}, billingInformation), { contact: __assign(__assign({}, billingInformation === null || billingInformation === void 0 ? void 0 : billingInformation.contact), getCardholderNameFromString(cardholderName)) }));
        }
    }, [cardholderName]);
    var onChangeBillingAddress = function (address, addressValidityState) {
        setBillingInformation(__assign(__assign({}, billingInformation), { address: address }));
        setBillingAddressValidityState(addressValidityState);
    };
    var onClickCallback = function () {
        var _a;
        props.onProcessing(true);
        var cardholderNameValid = ((cardholderName === null || cardholderName === void 0 ? void 0 : cardholderName.trim().length) || 0) > 0;
        setValidityState({
            cardholderNameValid: cardholderNameValid,
            creditCardValidity: cardValidityState,
            billingAddressValidity: billingAddressValidityState
        });
        if (!cardholderNameValid || !(cardValidityState === null || cardValidityState === void 0 ? void 0 : cardValidityState.isValid) || !(billingAddressValidityState === null || billingAddressValidityState === void 0 ? void 0 : billingAddressValidityState.isValid) || !creditCardPayload) {
            var warnResponse = {
                ok: false,
                status: 'warning',
                paymentMethod: SupportedPaymentMethodMappings.webDropInCreditCard,
                reason: 'Invalid Payment Information.'
            };
            // TODO: Refector to streamline handling response for all vendors.
            if (!(billingAddressValidityState === null || billingAddressValidityState === void 0 ? void 0 : billingAddressValidityState.isValid) && billingAddressValidityState) {
                var addressErrors = AddressErrorHandling(billingAddressValidityState);
                warnResponse = __assign(__assign({}, warnResponse), { errors: { billingInformation: { address: addressErrors } } });
            }
            props.onPaymentFailure(warnResponse);
            props.onProcessing(false);
            return;
        }
        var validatedCreditCardForm = {
            encryptedCustomerInput: {
                encryptedCardNumber: creditCardPayload.encryptedCardNumber,
                encryptedExpiryMonth: creditCardPayload.encryptedExpiryMonth,
                encryptedExpiryYear: creditCardPayload.encryptedExpiryYear,
                encryptedSecurityCode: creditCardPayload.encryptedSecurityCode,
                type: creditCardPayload.type
            },
            paymentType: SupportedPaymentMethods.WEB_DROP_IN_CREDIT_CARD,
            browserInfo: creditCardPayload.browserInfo,
            billingInformation: billingInformation,
            cardholderName: cardholderName,
            riskData: creditCardPayload.riskData
        };
        if (props.applicationSession) {
            props.applicationSession.config.billingInformation = billingInformation;
        }
        (_a = props.onSubmit) === null || _a === void 0 ? void 0 : _a.call(props, validatedCreditCardForm);
    };
    useEffect(function () {
        var _a, _b, _c;
        (_a = props.onCreditCardFormValid) === null || _a === void 0 ? void 0 : _a.call(props, (validityState === null || validityState === void 0 ? void 0 : validityState.cardholderNameValid) === true &&
            ((_b = validityState === null || validityState === void 0 ? void 0 : validityState.creditCardValidity) === null || _b === void 0 ? void 0 : _b.isValid) === true &&
            ((_c = validityState === null || validityState === void 0 ? void 0 : validityState.billingAddressValidity) === null || _c === void 0 ? void 0 : _c.isValid) === true);
    }, [validityState]);
    var invalidFields = {
        invalidCardholderName: validityState !== undefined && !(validityState === null || validityState === void 0 ? void 0 : validityState.cardholderNameValid),
        invalidCardNumber: validityState !== undefined && !((_a = validityState.creditCardValidity) === null || _a === void 0 ? void 0 : _a.validCreditCardNumber) && !(cardValidityState === null || cardValidityState === void 0 ? void 0 : cardValidityState.validCreditCardNumber),
        invalidCardBrand: !!cardValidityState && !(cardValidityState === null || cardValidityState === void 0 ? void 0 : cardValidityState.validCardBrand),
        invalidExpiry: validityState !== undefined && (!((_b = validityState.creditCardValidity) === null || _b === void 0 ? void 0 : _b.validExpiryMonth) || !((_c = validityState.creditCardValidity) === null || _c === void 0 ? void 0 : _c.validExpiryYear)),
        invalidCvv: validityState !== undefined && !((_d = validityState.creditCardValidity) === null || _d === void 0 ? void 0 : _d.validCVV)
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(CreditCardWithCardholderName, { ref: formRef },
            React.createElement(CreditCardFields, { showSecurityCodeContents: showSecurityCodeContents, securityCodeEmpty: securityCodeEmpty, showCardNumberContents: showCardNumberContents, cardNumberEmpty: cardNumberEmpty, showExpiryDateContents: showExpiryDateContents, expiryDateEmpty: expiryDateEmpty, supportedCardBrands: supportedCardBrands, selectedCardBrand: selectedCardBrand, cardholderName: cardholderName, setCardholderName: setCardholderName, invalidFields: invalidFields }),
            React.createElement(BillingAddressField, { vendor: props.vendor, existingAddress: (_e = props.existingBillingInformation) === null || _e === void 0 ? void 0 : _e.address, address: billingInformation === null || billingInformation === void 0 ? void 0 : billingInformation.address, onChange: onChangeBillingAddress, locale: props.locale, validityState: validityState === null || validityState === void 0 ? void 0 : validityState.billingAddressValidity }),
            props.hideContinueButton !== true && (React.createElement(SubmitButton, { id: "".concat(ComponentId.BUTTON_SUBMIT_PAYMENT, "_").concat(props.locale), onClick: onClickCallback, label: creditCardLocaleConfig.Labels.continueButton })))));
});
export default CreditCard;
var templateObject_1, templateObject_2;
